import { useState, useEffect } from 'react';
import { Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const defaultReviews = [
  {
    id: '1',
    name: 'Marie et Pierre',
    location: 'Paris, France',
    rating: 5,
    comment: 'Un sejour merveilleux dans l\'appartement Riesling ! L\'emplacement est ideal, au coeur du village, et l\'appartement est tres bien equipe. Nous reviendrons sans hesiter.',
    apartment: 'Riesling'
  },
  {
    id: '2',
    name: 'Hans & Greta',
    location: 'Munich, Allemagne',
    rating: 5,
    comment: 'Wunderschone Wohnung im Herzen von Riquewihr. Der Duplex Muscat ist geraumig und charmant. Herr Grewis ist sehr freundlich. Wir empfehlen es warmstens!',
    apartment: 'Muscat'
  },
  {
    id: '3',
    name: 'Familie Dupont',
    location: 'Lyon, France',
    rating: 5,
    comment: 'Vacances inoubliables en famille. Les enfants ont adore le marche de Noel. L\'appartement etait impeccable et la vue sur les maisons a colombages est un vrai bonheur.',
    apartment: 'Riesling'
  },
  {
    id: '4',
    name: 'John & Sarah',
    location: 'London, UK',
    rating: 5,
    comment: 'Absolutely charming apartment in a fairy-tale village. The wine route is incredible. We spent a wonderful week exploring Alsace from this perfect base.',
    apartment: 'Muscat'
  },
];

function StarRating({ rating }) {
  return (
    <div className="flex gap-1">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          size={14}
          className={i < rating ? 'fill-gold text-gold' : 'text-cream-400'}
        />
      ))}
    </div>
  );
}

export default function ReviewsSection() {
  const [reviews, setReviews] = useState(defaultReviews);
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const res = await axios.get(`${API}/reviews`);
        if (res.data.length > 0) {
          setReviews([...defaultReviews, ...res.data]);
        }
      } catch (e) {
        // Use default reviews
      }
    };
    fetchReviews();
  }, []);

  const prev = () => setCurrent((c) => (c - 1 + reviews.length) % reviews.length);
  const next = () => setCurrent((c) => (c + 1) % reviews.length);

  const visibleReviews = [];
  for (let i = 0; i < Math.min(3, reviews.length); i++) {
    visibleReviews.push(reviews[(current + i) % reviews.length]);
  }

  return (
    <section
      id="avis"
      data-testid="reviews-section"
      className="py-24 md:py-32 bg-cream-50 relative grain-overlay"
    >
      <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
        <div className="text-center mb-16 md:mb-20">
          <p className="font-accent text-wine-700 text-lg tracking-[0.2em] uppercase mb-4">
            Temoignages
          </p>
          <h2 className="font-display text-4xl md:text-5xl text-earth-100 mb-6">
            Ce que disent nos hotes
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {visibleReviews.map((review, idx) => (
            <div
              key={`${review.id}-${idx}`}
              data-testid={`review-card-${idx}`}
              className="bg-white border border-cream-400 p-8 flex flex-col hover:shadow-md transition-shadow duration-300"
            >
              <Quote size={28} className="text-wine-700/20 mb-4" />
              <p className="font-body text-earth-200 leading-relaxed flex-1 mb-6 italic">
                "{review.comment}"
              </p>
              <div className="border-t border-cream-400 pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-display text-base text-earth-100">{review.name}</p>
                    <p className="font-body text-xs text-earth-300">{review.location}</p>
                  </div>
                  <div className="text-right">
                    <StarRating rating={review.rating} />
                    {review.apartment && (
                      <p className="font-body text-xs text-wine-700 mt-1">{review.apartment}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-center gap-4">
          <button
            onClick={prev}
            data-testid="reviews-prev-btn"
            className="w-12 h-12 border border-cream-400 flex items-center justify-center hover:bg-wine-700 hover:text-white hover:border-wine-700 transition-all duration-300 text-earth-200"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={next}
            data-testid="reviews-next-btn"
            className="w-12 h-12 border border-cream-400 flex items-center justify-center hover:bg-wine-700 hover:text-white hover:border-wine-700 transition-all duration-300 text-earth-200"
          >
            <ChevronRight size={20} />
          </button>
        </div>
      </div>
    </section>
  );
}
